export const FLIGHT_FARE_TYPE_VALUES = [
  "regularFare",
  "studentFare",
  "umrahFare",
  "citizensFare",
] as const;

export type FlightFareType = (typeof FLIGHT_FARE_TYPE_VALUES)[number];
export type FlightAdultPassengerType = "ADT" | "STU" | "ADR" | "SRC";

export const DEFAULT_FLIGHT_FARE_TYPE: FlightFareType = "regularFare";
export const SENIOR_CITIZEN_MIN_AGE = 60;

export const FLIGHT_FARE_TYPE_OPTIONS: ReadonlyArray<{
  key: FlightFareType;
  label: string;
}> = [
  { key: "regularFare", label: "Regular Fare" },
  { key: "studentFare", label: "Student Fare" },
  { key: "umrahFare", label: "Umrah Fare" },
  { key: "citizensFare", label: "Senior Citizens" },
] as const;

const FARE_TO_PASSENGER_CODE: Record<FlightFareType, FlightAdultPassengerType> = {
  regularFare: "ADT",
  studentFare: "STU",
  umrahFare: "ADR",
  citizensFare: "SRC",
};

const PASSENGER_TYPE_LABELS: Record<FlightAdultPassengerType, string> = {
  ADT: "Adult",
  STU: "Student",
  ADR: "Umrah",
  SRC: "Senior Citizen",
};

const FARE_TYPE_ALIASES: Record<string, FlightFareType> = {
  adt: "regularFare",
  adr: "umrahFare",
  citizens: "citizensFare",
  citizensfare: "citizensFare",
  regular: "regularFare",
  regularfare: "regularFare",
  senior: "citizensFare",
  seniorcitizen: "citizensFare",
  seniorcitizens: "citizensFare",
  src: "citizensFare",
  student: "studentFare",
  studentfare: "studentFare",
  stu: "studentFare",
  umrah: "umrahFare",
  umrahfare: "umrahFare",
};

const ADULT_EQUIVALENT_PASSENGER_TYPES = new Set<FlightAdultPassengerType>([
  "ADT",
  "STU",
  "ADR",
  "SRC",
]);

export function normalizeFlightFareType(value?: string | null): FlightFareType {
  if (!value) {
    return DEFAULT_FLIGHT_FARE_TYPE;
  }

  const normalizedValue = value.trim();
  if (FLIGHT_FARE_TYPE_VALUES.includes(normalizedValue as FlightFareType)) {
    return normalizedValue as FlightFareType;
  }

  return (
    FARE_TYPE_ALIASES[normalizedValue.toLowerCase()] ??
    DEFAULT_FLIGHT_FARE_TYPE
  );
}

export function renderPassengerCode(
  type: FlightFareType | string | undefined | null,
): FlightAdultPassengerType {
  return FARE_TO_PASSENGER_CODE[normalizeFlightFareType(type)];
}

export function isRegularFlightFareType(
  type: FlightFareType | string | undefined | null,
) {
  return normalizeFlightFareType(type) === DEFAULT_FLIGHT_FARE_TYPE;
}

export function isAdultEquivalentPassengerType(value: string): boolean {
  return ADULT_EQUIVALENT_PASSENGER_TYPES.has(
    value.toUpperCase() as FlightAdultPassengerType,
  );
}

export function getPassengerTypeLabel(type: string): string {
  const normalizedType = type.toUpperCase();

  if (isAdultEquivalentPassengerType(normalizedType)) {
    return PASSENGER_TYPE_LABELS[normalizedType as FlightAdultPassengerType];
  }

  if (normalizedType === "INF") {
    return "Infant";
  }

  if (
    normalizedType === "CHD" ||
    normalizedType === "CNN" ||
    /^C\d{2}$/.test(normalizedType)
  ) {
    return "Child";
  }

  return type;
}
